import { graphql } from '@/gql';
import type { TicketKind, TicketSaleChannel, TicketsQuery, TicketStatus } from '@/gql/graphql';
import { optionsOf } from '@/hooks/useUrlState';
import { ticketKindLabel } from '@/lib/labels';

export const CHANGE_COMPANION_EMAIL_CONSEQUENCE =
  "The ticket moves to the new claimer address. The previous claimer's link will stop working immediately and permanently, and a fresh claim link will be emailed to the new address. The buyer's address stays as it is.";

/** Matches the server: claimed, imported and door tickets cannot change address. */
export function canChangeCompanionEmail(ticket: {
  status: TicketStatus;
  kind: TicketKind;
}): boolean {
  return ticket.status !== 'CLAIMED' && ticket.status !== 'IMPORTED' && ticket.kind !== 'DOOR';
}

export type AdminTicketGridRow = TicketsQuery['tickets']['nodes'][number];

/** The statuses the filter offers, with their labels: the Select options and the `/tickets` `status` codec both read this. */
export const TICKET_STATUS_FILTER_LABELS: Record<TicketStatus, string> = {
  HELD: 'Held',
  PURCHASED: 'Purchased (unclaimed)',
  CLAIMED: 'Claimed',
  RELEASED: 'Released',
  REFUNDED: 'Refunded',
  BLOCKED: 'Blocked',
  IMPORTED: 'Imported',
};
export const TICKET_STATUS_FILTER_OPTIONS = optionsOf(TICKET_STATUS_FILTER_LABELS, 'Any status');

/** The types the filter offers; a guest-list ticket is a "Guest" in the admin (the enum stays GUEST_LIST). */
export const TICKET_KIND_FILTER_LABELS: Record<TicketKind, string> = {
  ONLINE: ticketKindLabel('ONLINE'),
  POS: ticketKindLabel('POS'),
  MEMBER: ticketKindLabel('MEMBER'),
  DOOR: ticketKindLabel('DOOR'),
  GUEST_LIST: ticketKindLabel('GUEST_LIST'),
};
export const TICKET_KIND_FILTER_OPTIONS = optionsOf(TICKET_KIND_FILTER_LABELS, 'Any type');

/** How a ticket was sold, with its label: the Channel column, the detail row, and the `/tickets` `channel` filter read this. */
export const TICKET_SALE_CHANNEL_LABELS: Record<TicketSaleChannel, string> = {
  WEBSITE: 'Website',
  APP: 'App',
  LINK: 'Link',
  MEMBER: 'Member',
  POS: 'POS',
  DOOR: 'Door',
  GUEST_LIST: 'Guest list',
};
export const TICKET_SALE_CHANNEL_FILTER_OPTIONS = optionsOf(TICKET_SALE_CHANNEL_LABELS, 'Any channel');
export const ticketSaleChannelLabel = (channel: TicketSaleChannel): string => TICKET_SALE_CHANNEL_LABELS[channel];

export const TicketsDoc = graphql(/* GraphQL */ `
  query Tickets($filter: TicketLookupFilter, $page: Int, $pageSize: Int) {
    tickets(filter: $filter, page: $page, pageSize: $pageSize) {
      nodes {
        id
        nightId
        nightTitle
        stageId
        stageName
        status
        kind
        saleChannel
        buyerEmail
        claimerEmail
        holderEmail
        purchasedAt
        claimedAt
        orderId
        createdAt
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

export const AdminTicketDoc = graphql(/* GraphQL */ `
  query AdminTicket($id: UUID!) {
    adminTicket(id: $id) {
      id
      nightId
      nightTitle
      stageId
      stageName
      status
      kind
      saleChannel
      buyerEmail
      claimerEmail
      holderEmail
      purchasedAt
      claimedAt
      refundedAt
      releasedAt
      holdExpiresAt
      externalCode
      orderId
      createdAt
      updatedAt
      recognitionDate
      ticketPrice
      feePrice
      userId
      claim {
        firstName
        lastName
        dateOfBirth
        phone
        city
        country
        userId
        attested
        claimedAt
      }
      payments {
        id
        status
        paymentMethod
        amount
      }
      entranceLogs {
        id
        scannedAt
        kind
        result
        guestName
        scannerName
        refuseReason
        refuseNote
        recognitionDate
      }
      blocks {
        id
        reason
        note
        blockedAt
        blockedByUserId
        unblockedAt
        unblockedByUserId
      }
      emailChanges {
        id
        previousEmail
        newEmail
        actorKind
        actorUserId
        createdAt
      }
    }
  }
`);

export const ChangeCompanionEmailDoc = graphql(/* GraphQL */ `
  mutation ChangeCompanionEmail($ticketId: UUID!, $email: String!) {
    changeCompanionEmail(ticketId: $ticketId, email: $email) {
      id
    }
  }
`);

export const CreateManageTicketsLinkDoc = graphql(/* GraphQL */ `
  mutation CreateManageTicketsLink($ticketId: UUID!, $who: ManageLinkFor!) {
    createManageTicketsLink(ticketId: $ticketId, who: $who)
  }
`);

export const RELEASE_HELD_TICKET_CONSEQUENCE =
  'Stock for this ticket is freed. A payment already started is not cancelled.';

export const ReleaseHeldTicketDoc = graphql(/* GraphQL */ `
  mutation ReleaseHeldTicket($id: UUID!) {
    releaseHeldTicket(id: $id) {
      id
      status
      releasedAt
      holdExpiresAt
    }
  }
`);

export const BLOCK_TICKET_CONSEQUENCE =
  'The ticket is refused at the door, its QR stops working and its seat is freed. The holder keeps the ticket on record until it is unblocked.';

export const UNBLOCK_TICKET_CONSEQUENCE =
  'The ticket works at the door again and its seat is taken again if it is still available. It is refused if the stage or night is sold out, or if the holder already has another ticket.';

export const BlockTicketDoc = graphql(/* GraphQL */ `
  mutation BlockTicket($input: BlockTicketInput!) {
    blockTicket(input: $input) {
      id
      status
    }
  }
`);

export const UnblockTicketDoc = graphql(/* GraphQL */ `
  mutation UnblockTicket($id: UUID!) {
    unblockTicket(id: $id) {
      id
      status
    }
  }
`);

/** Matches the server: only purchased, claimed or imported tickets can be blocked (never door tickets). */
export function canBlockTicket(ticket: { status: TicketStatus; kind: TicketKind }): boolean {
  return (
    (ticket.status === 'PURCHASED' ||
      ticket.status === 'CLAIMED' ||
      ticket.status === 'IMPORTED') &&
    ticket.kind !== 'DOOR'
  );
}

export const NightsForTicketsFilterDoc = graphql(/* GraphQL */ `
  query NightsForTicketsFilter {
    nights(page: 1, pageSize: 200, sort: { field: STARTS_AT, direction: DESC }) {
      nodes {
        id
        title
        startsAt
      }
    }
  }
`);
